import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Timer, Zap, Trophy, Star, RotateCcw, CheckCircle2, XCircle, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

const TOTAL_TIME = 60; // 1 dakika

export default function VocabularyTimeRace({
  cards, // [{front: 'Hello', back: 'Merhaba'}, ...]
  title,
  onAnswer,
  disabled = false
}) {
  const [gameState, setGameState] = useState('ready'); // ready, playing, finished
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [options, setOptions] = useState([]);
  const [shuffledCards, setShuffledCards] = useState([]);
  const timerRef = useRef(null);
  const { playCorrect, playIncorrect, playCombo, playLevelComplete, playClick, playWelcome } = useSounds();

  // Seçenekleri oluştur
  const generateOptions = useCallback((cardArray, index) => {
    const currentCard = cardArray[index];
    if (!currentCard) return;

    // Doğru cevap + 3 yanlış cevap
    const wrongAnswers = cardArray
      .filter((_, i) => i !== index)
      .map(c => c.back)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);

    const allOptions = [...wrongAnswers, currentCard.back]
      .sort(() => Math.random() - 0.5);

    setOptions(allOptions);
  }, []);

  // Kartları karıştır ve seçenekleri hazırla
  const prepareGame = useCallback(() => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentIndex(0);
    generateOptions(shuffled, 0);
  }, [cards, generateOptions]);

  // Oyunu başlat
  const startGame = () => {
    playWelcome();
    setGameState('playing');
    setTimeLeft(TOTAL_TIME);
    setScore(0);
    setCorrectCount(0);
    setWrongCount(0);
    setCombo(0);
    setMaxCombo(0);
    setSelectedAnswer(null);
    setIsCorrect(null);
    prepareGame();
  };

  // Timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setGameState('finished');
            playLevelComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [gameState, timeLeft, playLevelComplete]);

  // Cevap seçildiğinde
  const handleAnswer = (answer) => {
    if (selectedAnswer !== null || gameState !== 'playing') return;

    playClick();
    const currentCard = shuffledCards[currentIndex];
    const correct = answer === currentCard.back;
    
    setSelectedAnswer(answer);
    setIsCorrect(correct);

    if (correct) {
      playCorrect();
      // Hız bonusu hesapla - ne kadar çabuk cevap verirse o kadar çok puan
      const timeBonus = Math.ceil(timeLeft / 10); // Kalan süreye göre bonus
      const comboBonus = combo * 5; // Combo bonusu
      const basePoints = 10;
      const totalPoints = basePoints + timeBonus + comboBonus;

      setScore(prev => prev + totalPoints);
      setCorrectCount(prev => prev + 1);
      setCombo(prev => {
        const newCombo = prev + 1;
        if (newCombo > maxCombo) setMaxCombo(newCombo);
        // Combo sesi - her 3 ardışık doğruda
        if (newCombo > 0 && newCombo % 3 === 0) {
          playCombo();
        }
        return newCombo;
      });
    } else {
      playIncorrect();
      setWrongCount(prev => prev + 1);
      setCombo(0);
    }

    // Sonraki soruya geç
    setTimeout(() => {
      if (currentIndex < shuffledCards.length - 1) {
        const nextIndex = currentIndex + 1;
        setCurrentIndex(nextIndex);
        generateOptions(shuffledCards, nextIndex);
        setSelectedAnswer(null);
        setIsCorrect(null);
      } else {
        // Tüm kartlar bitti
        setGameState('finished');
        playLevelComplete();
      }
    }, 600);
  };

  // Ses çal
  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Oyun bitti
  useEffect(() => {
    if (gameState === 'finished' && onAnswer) {
      const accuracy = shuffledCards.length > 0 ? correctCount / shuffledCards.length : 0;
      onAnswer(accuracy >= 0.6, accuracy);
    }
  }, [gameState, onAnswer, shuffledCards.length, correctCount]);

  // Başlangıç ekranı
  if (gameState === 'ready') {
    return (
      <div className="w-full max-w-lg mx-auto text-center">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl p-8 shadow-2xl text-white">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Timer className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold mb-2">⏱️ Zamanla Yarış!</h2>
          <p className="text-white/80 mb-6">{title || 'Kelime Testi'}</p>
          
          <div className="bg-white/10 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold mb-2">🎯 Kurallar:</h3>
            <ul className="text-sm text-white/90 space-y-1">
              <li>• 1 dakikan var!</li>
              <li>• Hızlı cevapla = Daha çok puan</li>
              <li>• Art arda doğrular = Combo bonus</li>
              <li>• {cards.length} soru seni bekliyor</li>
            </ul>
          </div>

          <Button
            onClick={startGame}
            className="w-full py-6 text-xl font-bold bg-white text-purple-600 hover:bg-white/90 rounded-xl shadow-lg"
          >
            <Zap className="w-6 h-6 mr-2" />
            BAŞLA!
          </Button>
        </div>
      </div>
    );
  }

  // Sonuç ekranı
  if (gameState === 'finished') {
    const accuracy = shuffledCards.length > 0 ? Math.round((correctCount / shuffledCards.length) * 100) : 0;
    const grade = accuracy >= 90 ? 'S' : accuracy >= 80 ? 'A' : accuracy >= 70 ? 'B' : accuracy >= 60 ? 'C' : 'D';
    const gradeColors = {
      S: 'from-yellow-400 to-amber-500',
      A: 'from-green-400 to-emerald-500',
      B: 'from-blue-400 to-cyan-500',
      C: 'from-purple-400 to-pink-500',
      D: 'from-gray-400 to-gray-500'
    };

    return (
      <div className="w-full max-w-lg mx-auto text-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
          {/* Grade Badge */}
          <div className={cn(
            'w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-br',
            gradeColors[grade]
          )}>
            <span className="text-4xl font-black text-white">{grade}</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {accuracy >= 80 ? '🎉 Harika!' : accuracy >= 60 ? '👍 İyi!' : '💪 Tekrar Dene!'}
          </h2>
          <p className="text-gray-500 mb-6">Zamanla Yarış Tamamlandı</p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-100">
              <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-800">{score}</p>
              <p className="text-xs text-gray-500">Toplam Puan</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
              <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-800">{correctCount}/{shuffledCards.length}</p>
              <p className="text-xs text-gray-500">Doğru</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
              <Zap className="w-6 h-6 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-800">x{maxCombo}</p>
              <p className="text-xs text-gray-500">Max Combo</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-100">
              <Star className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-800">%{accuracy}</p>
              <p className="text-xs text-gray-500">Başarı</p>
            </div>
          </div>

          <Button
            onClick={startGame}
            className="w-full py-5 text-lg font-bold bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 rounded-xl shadow-lg"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Tekrar Oyna
          </Button>
        </div>
      </div>
    );
  }

  // Oyun ekranı
  const currentCard = shuffledCards[currentIndex];
  const timePercentage = (timeLeft / TOTAL_TIME) * 100;
  const timeColor = timeLeft > 30 ? 'bg-green-500' : timeLeft > 10 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Header */}
      <div className="mb-6">
        {/* Timer Bar */}
        <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden mb-3">
          <div
            className={cn('h-full transition-all duration-1000 ease-linear', timeColor)}
            style={{ width: `${timePercentage}%` }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-bold text-white drop-shadow">{timeLeft}s</span>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">
              {currentIndex + 1}/{shuffledCards.length}
            </span>
          </div>
          
          {combo > 1 && (
            <div className="flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full animate-pulse">
              <Zap className="w-4 h-4" />
              <span className="font-bold">x{combo} COMBO!</span>
            </div>
          )}
          
          <div className="flex items-center gap-1 bg-yellow-100 px-3 py-1 rounded-full">
            <Trophy className="w-4 h-4 text-yellow-600" />
            <span className="font-bold text-yellow-700">{score}</span>
          </div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-400">Bu kelimenin Türkçesi nedir?</span>
          <button
            onClick={() => speakText(currentCard?.front)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Volume2 className="w-5 h-5 text-blue-500" />
          </button>
        </div>
        <h2 className="text-3xl font-bold text-center text-gray-800 py-4">
          {currentCard?.front}
        </h2>
        {currentCard?.phonetic && (
          <p className="text-center text-gray-400 text-sm">{currentCard.phonetic}</p>
        )}
      </div>

      {/* Answer Options */}
      <div className="grid grid-cols-2 gap-3">
        {options.map((option, index) => {
          const isSelected = selectedAnswer === option;
          const isCorrectAnswer = currentCard?.back === option;
          
          let buttonClass = 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50';
          
          if (selectedAnswer !== null) {
            if (isCorrectAnswer) {
              buttonClass = 'bg-green-100 border-2 border-green-500 text-green-700';
            } else if (isSelected && !isCorrect) {
              buttonClass = 'bg-red-100 border-2 border-red-500 text-red-700';
            } else {
              buttonClass = 'bg-gray-100 border-2 border-gray-200 text-gray-400';
            }
          }

          return (
            <button
              key={index}
              onClick={() => handleAnswer(option)}
              disabled={selectedAnswer !== null}
              className={cn(
                'p-4 rounded-xl font-medium text-center transition-all duration-200 transform active:scale-95',
                buttonClass
              )}
            >
              <span className="flex items-center justify-center gap-2">
                {selectedAnswer !== null && isCorrectAnswer && (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
                {selectedAnswer !== null && isSelected && !isCorrect && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                {option}
              </span>
            </button>
          );
        })}
      </div>

      {/* Progress Dots */}
      <div className="flex justify-center gap-1 mt-6 flex-wrap max-w-md mx-auto">
        {shuffledCards.slice(0, 20).map((_, index) => (
          <div
            key={index}
            className={cn(
              'w-2 h-2 rounded-full transition-all',
              index < currentIndex ? 'bg-green-500' :
              index === currentIndex ? 'bg-blue-500 scale-125' :
              'bg-gray-200'
            )}
          />
        ))}
        {shuffledCards.length > 20 && (
          <span className="text-xs text-gray-400 ml-1">+{shuffledCards.length - 20}</span>
        )}
      </div>
    </div>
  );
}
